<script lang="ts">
  import CommunityNotificationPreferences from "$components/community/NotificationPreferences.svelte";
  import Dialogue from "$components/+blocks/Dialogue/Dialogue.svelte";
  import Modal from "$components/+blocks/Modal/Modal.svelte";
  import Dropdown from "$components/+blocks/Dropdown/Dropdown.svelte";
  import CommunityJoinCard from "./CommunityJoinCard/CommunityJoinCard.svelte";
  import CommunityLogo from "./CommunityLogo.svelte";
  import Link from "$components/+blocks/Dropdown/fields/Link.svelte";
  import Action from "$components/+blocks/Dropdown/fields/Action.svelte";
  import IconButtonTrigger from "$components/+blocks/Dropdown/fields/IconButtonTrigger.svelte";
  import { alerts } from "$stores/alert";
  import { isUserAuthed, user } from "$stores/auth";
  import {
    community,
    communitySlug,
    trustedWorkspaceFlag,
    isCommunityEnabled,
  } from "$stores/community";
  import { goto } from "$app/navigation";
  import {
    canEditCommunitySettings,
    canInviteMembers,
    isCommunityMember,
  } from "$stores/permissions";
  import { fade } from "svelte/transition";
  import { page } from "$app/stores";
  import { basePlatformUserHomeUrl, communitySettingsURL } from "app/utils/urls";
  import { Bell, Cog, Dots, LA, PlusPerson } from "$components/+blocks/OVIcon";
  import cx from "classnames";
  import api from "api";

  const queryParameters = $page.url.searchParams;

  let isJoinCommunityModalOpen: boolean = false;
  let isLeaveCommunityModalOpen: boolean = false;
  let heading: HTMLHeadingElement;
  let isOverflowing: boolean = false;

  // mask the right side of the heading if it is overflowing whenever the heading changes
  $: {
    (isOverflowing = heading?.clientWidth < heading?.scrollWidth), $community.name;
  }
  $: isNotificationPreferencesModalOpen = !!(
    $isUserAuthed && queryParameters.get("channel-preferences")
  );
</script>

<div class="relative flex w-full flex-col gap-2">
  <!-- Cheeky hard-code rives logo to link back to their main website -->
  {#if $communitySlug === "rive"}
    <a
      class={cx(
        "flex w-full items-center gap-3 rounded-lg border-[0.5px] border-transparent transition-colors sm:p-1.5 sm:pl-[18px] md:border-0"
      )}
      target="_blank"
      rel="noopener noreferrer"
      href="https://rive.app"
    >
      <CommunityLogo />
      <h3
        class="hidden w-full overflow-hidden text-sm leading-[18px] font-semibold whitespace-nowrap text-neutral-800 sm:block md:text-[13px] dark:text-neutral-200"
        class:[mask-image:_linear-gradient(to_left,_transparent_2%,_black_17%)]={isOverflowing}
        title={$community.name}
        in:fade={{ duration: 80 }}
        bind:this={heading}
      >
        {$community.name}
      </h3>
      {#if $isCommunityMember && $isCommunityEnabled}
        <div class="pointer-events-auto ml-auto hidden sm:flex">
          <Dropdown>
            <IconButtonTrigger
              slot="trigger"
              size="small"
              let:active
              {active}
              icon={Dots}
              a11yLabel="Community options"
            />
            {#if $canEditCommunitySettings}
              <Link
                href={communitySettingsURL({
                  communitySlug: $communitySlug,
                  section: "general",
                  subSection: "overview",
                })}
                title="Settings"
                icon={Cog}
              />
            {/if}
            <Action
              title="Notification preferences"
              icon={Bell}
              on:click={() => (isNotificationPreferencesModalOpen = true)}
            />
            {#if $isCommunityEnabled && $canInviteMembers && $trustedWorkspaceFlag}
              <Action
                title="Invite member"
                icon={PlusPerson}
                on:click={() => (isJoinCommunityModalOpen = true)}
              />
            {/if}
            <Action
              title="Leave community"
              icon={LA}
              danger
              on:click={() => (isLeaveCommunityModalOpen = true)}
            />
          </Dropdown>
        </div>
      {/if}
    </a>
  {:else}
    <!-- tabIndex makes HTML elements focusable -->
    <div
      class="flex w-full items-center gap-3 rounded-lg border-[0.5px] border-transparent transition-colors sm:p-1.5 sm:pl-[18px] md:border-0"
    >
      <CommunityLogo />
      <h3
        class="hidden w-full overflow-hidden text-sm leading-[18px] font-semibold whitespace-nowrap text-neutral-800 sm:block md:text-[13px] dark:text-neutral-200"
        class:[mask-image:_linear-gradient(to_left,_transparent_2%,_black_17%)]={isOverflowing}
        title={$community.name}
        in:fade={{ duration: 80 }}
        bind:this={heading}
      >
        {$community.name}
      </h3>
      {#if $isCommunityMember && $isCommunityEnabled}
        <div class="pointer-events-auto ml-auto hidden sm:flex">
          <Dropdown>
            <IconButtonTrigger
              slot="trigger"
              size="small"
              let:active
              {active}
              icon={Dots}
              a11yLabel="Community options"
            />
            <Action
              title="Notification preferences"
              icon={Bell}
              on:click={() => (isNotificationPreferencesModalOpen = true)}
            />
            {#if $canInviteMembers}
              <Action
                title="Invite member"
                icon={PlusPerson}
                on:click={() => (isJoinCommunityModalOpen = true)}
              />
            {/if}
            <Action
              title="Leave community"
              icon={LA}
              danger
              on:click={() => (isLeaveCommunityModalOpen = true)}
            />
          </Dropdown>
        </div>
      {/if}
    </div>
  {/if}

  <Modal bind:open={isJoinCommunityModalOpen} maxWidth={"max-w-[500px]"} withCloseIcon>
    <CommunityJoinCard />
  </Modal>

  <CommunityNotificationPreferences bind:open={isNotificationPreferencesModalOpen} />

  <Dialogue
    title={"Leave community"}
    message={`Are you sure you want to leave ${$community.name}?`}
    bind:open={isLeaveCommunityModalOpen}
    confirmLabel={"Leave"}
    danger
    on:confirm={async () => {
      try {
        if ($communitySlug && $user) {
          await api.communitiesMembersDestroy({
            communitySlug: $communitySlug,
            uuid: $user.uuid,
          });
          goto(basePlatformUserHomeUrl(), { state: { leftCommunity: $community.name } });
        }
      } catch (e) {
        alerts.error(`Unable to leave ${$communitySlug} community`);
      }
    }}
  />
</div>
